/**
 * DragOverlay - Overlay visual durante drag & drop
 * Exibe feedback visual quando arquivos são arrastados sobre o chat
 */

import React from 'react';
import { Icon } from '@/shared/icons'
interface DragOverlayProps {
  /** Se o overlay deve ser exibido */
  isVisible: boolean;
  /** Mensagem customizada */
  message?: string;
}

/**
 * Componente de overlay para drag & drop
 *
 * @example
 * ```tsx
 * <DragOverlay isVisible={isDragging} message="Solte os arquivos aqui" />
 * ```
 */
const DragOverlay: React.FC<DragOverlayProps> = ({
  isVisible,
  message = 'Solte os arquivos aqui',
}) => {
  if (!isVisible) return null;

  return (
    <div className="absolute inset-0 z-50 pointer-events-none">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-indigo-600 bg-opacity-10 backdrop-blur-sm" />

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-indigo-600 border-dashed p-12 max-w-md mx-4">
          {/* Icons grid */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-xl">
              <Icon name="image" className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-xl">
              <Icon name="video" className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-xl">
              <Icon name="music" className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-xl">
              <Icon name="file" className="w-8 h-8 text-indigo-600" />
            </div>
          </div>

          {/* FiUpload icon */}
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center animate-pulse">
                <Icon name="upload" className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-indigo-600 rounded-full animate-ping opacity-20" />
            </div>
          </div>

          {/* Message */}
          <p className="text-center text-xl font-semibold text-gray-900 mb-2">{message}</p>
          <p className="text-center text-sm text-gray-600">
            Imagens, vídeos, áudios e documentos
          </p>

          {/* Animated indicators */}
          <div className="flex justify-center gap-2 mt-6">
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

DragOverlay.displayName = 'DragOverlay';

export default DragOverlay;
